import React, { useEffect, useId, useRef, useState } from 'react';

const MIN_MS = 1400;
const ADMIN_PASSWORD = 'Bel014786';

export default function ExplorerSplash({ progress = 0, phase = 'loading', onRetry, gated = false, onUnlock }) {
  const started = useRef(Date.now());
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const [bar, setBar] = useState(4);
  const targetRef = useRef(8);

  useEffect(() => {
    targetRef.current =
      phase === 'ready' ? 100 : phase === 'error' ? Math.max(bar, 12) : Math.max(8, Math.min(96, progress || 8));
  }, [progress, phase, bar]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setBar((current) => {
        const target = targetRef.current;
        const next = current + (target - current) * 0.16;
        return Math.abs(next - target) < 0.35 ? target : next;
      });
    }, 40);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (phase !== 'ready' || leaving || gone) return undefined;
    const wait = Math.max(0, MIN_MS - (Date.now() - started.current));
    const start = window.setTimeout(() => setLeaving(true), wait);
    return () => window.clearTimeout(start);
  }, [phase, leaving, gone]);

  useEffect(() => {
    if (!leaving) return undefined;
    const end = window.setTimeout(() => setGone(true), 720);
    return () => window.clearTimeout(end);
  }, [leaving]);

  const passwordId = useId();
  const [showAdmin, setShowAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [adminError, setAdminError] = useState('');

  if (gone && !gated) return null;

  const label =
    gated
      ? 'Coming soon'
      : phase === 'error'
        ? 'The landscape could not open'
        : phase === 'ready'
          ? 'Welcome in'
          : bar < 20
            ? 'Preparing the grounds'
            : bar < 70
              ? 'Opening the gardens'
              : 'Almost there';

  const submitAdmin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAdminError('');
      onUnlock?.();
      return;
    }
    setAdminError('That password isn’t recognised.');
  };

  return (
    <div
      className={`be-splash${leaving && !gated ? ' is-leaving' : ''}`}
      role="status"
      aria-live="polite"
      aria-busy={!gated && phase === 'loading'}
    >
      <div className="be-splash-glow" aria-hidden="true" />
      <div className="be-splash-inner">
        <p className="be-splash-kicker">Belvoir Castle &amp; Gardens</p>
        <h1>A little world to explore</h1>
        <div className="be-splash-track">
          <span style={{ width: gated ? '100%' : `${Math.round(bar)}%` }} />
        </div>
        <p className="be-splash-status">
          {gated || phase === 'error' ? label : `${label}${phase === 'loading' ? ` · ${Math.round(bar)}%` : ''}`}
        </p>
        {phase === 'error' && !gated && (
          <button type="button" className="be-splash-retry" onClick={onRetry}>
            Try again
          </button>
        )}
        {gated && (
          <div className="be-splash-admin">
            {!showAdmin ? (
              <button type="button" className="be-splash-admin-toggle" onClick={() => setShowAdmin(true)}>
                Admin access
              </button>
            ) : (
              <form className="be-splash-admin-form" onSubmit={submitAdmin}>
                <label htmlFor={passwordId}>Password</label>
                <input
                  id={passwordId}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (adminError) setAdminError('');
                  }}
                />
                {adminError && (
                  <p className="be-splash-admin-error" role="alert">
                    {adminError}
                  </p>
                )}
                <button type="submit" className="be-splash-retry">
                  Enter
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
